import { omit } from "lodash";

import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  AppointmentType,
  AppointmentTypeInput,
  AppointmentTypesFilters,
  AppointmentTypesResponse
} from "./types/appointment-types.type";

const APPOINTMENT_TYPES_QUERY =
  "{id,name,color,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{product{id,cpt_code{id,code},display_name,price}},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,limit_to_appointment_blocks,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},is_virtual}";
export const APPOINTMENT_TYPES_QUERY_WITH_DEFAULT_NOTES =
  "{id,name,color,default_appointment_notes,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{product{id,cpt_code{id,code},display_name,price}},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,limit_to_appointment_blocks,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},is_virtual}";
const GET_APPOINTMENT_TYPE_QUERY =
  "{id,name,color,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{quantity,product{id,cpt_code{id,code},display_name,price,is_active,type}},notification_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_request_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_acknowledgment_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},appointment_information_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,google_review_request_message_disabled,ws_title,ws_description,ws_configuration,is_web_schedule,is_clinic_schedule,limit_to_appointment_blocks,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},patient_visibility,is_virtual,ambient_scribe_template_id,reservation_deposit_amount}";
export const GET_APPOINTMENT_TYPE_QUERY_WITH_DEFAULT_NOTES =
  "{id,name,color,default_appointment_notes,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{quantity,product{id,cpt_code{id,code},display_name,price,is_active,type}},notification_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_request_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_acknowledgment_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},appointment_information_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,google_review_request_message_disabled,ws_title,ws_description,ws_configuration,is_web_schedule,is_clinic_schedule,limit_to_appointment_blocks,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},patient_visibility,is_virtual,reservation_deposit_amount}";
export const GET_APPOINTMENT_TYPE_QUERY_WITH_DEFAULT_NOTES_AND_TEMPLATE =
  "{id,name,color,default_appointment_notes,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{quantity,product{id,cpt_code{id,code},display_name,price,is_active,type}},notification_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_request_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_acknowledgment_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},appointment_information_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,google_review_request_message_disabled,ws_title,ws_description,ws_configuration,is_web_schedule,is_clinic_schedule,limit_to_appointment_blocks,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},patient_visibility,is_virtual,ambient_scribe_template_id,reservation_deposit_amount}";

export const appointmentTypesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getAppointmentTypes: builder.query<
      AppointmentTypesResponse,
      AppointmentTypesFilters
    >({
      query: ({
        is_active,
        type,
        query = APPOINTMENT_TYPES_QUERY,
        exclude_type,
        is_clinic_schedule,
        is_web_schedule
      } = {}) => ({
        url: `/appointment-types/?${formatValidQueryParamUrlCommaSeparated({
          type,
          is_active,
          query,
          exclude_type,
          is_clinic_schedule,
          is_web_schedule
        })}`
      }),
      providesTags: (result) =>
        result?.map((appointmentType) => ({
          type: "AppointmentTypes" as const,
          id: appointmentType.id
        })) ?? []
    }),
    getAppointmentType: builder.query<
      AppointmentType,
      { id: number; query?: string }
    >({
      query: ({ id, query = GET_APPOINTMENT_TYPE_QUERY }) =>
        `/appointment-types/${id}/?query=${query}`,
      providesTags: (result) => [
        "AppointmentTypes",
        { type: "AppointmentTypes", id: result?.id }
      ]
    }),
    addAppointmentType: builder.mutation<AppointmentType, AppointmentTypeInput>(
      {
        query: (appointmentTypeData) => ({
          url: "/appointment-types/",
          method: "POST",
          body: appointmentTypeData
        }),
        invalidatesTags: ["AppointmentTypes"]
      }
    ),
    editAppointmentType: builder.mutation<
      AppointmentType,
      AppointmentTypeInput
    >({
      query: (appointmentTypeData) => ({
        url: `/appointment-types/${appointmentTypeData.id}/`,
        method: "PATCH",
        body: omit(appointmentTypeData, "id")
      }),
      invalidatesTags: ["AppointmentTypes"]
    }),
    resetTelehealthTemplates: builder.mutation<
      { reset_count: number },
      { id: number; message_types?: string[] }
    >({
      query: ({ id, message_types }) => ({
        url: `/appointment-types/${id}/reset-telehealth-templates/`,
        method: "POST",
        ...(message_types?.length && { body: { message_types } })
      }),
      invalidatesTags: ["AppointmentTypes", "MessageTemplates"]
    })
  })
});

export const {
  useGetAppointmentTypesQuery,
  useAddAppointmentTypeMutation,
  useEditAppointmentTypeMutation,
  useGetAppointmentTypeQuery,
  useResetTelehealthTemplatesMutation
} = appointmentTypesApi;
